'use client'

import { useVideo } from '@/context/VideoContext'
import { useVideoElement } from '@/hooks/useVideoElement'
import { useEffect, useRef } from 'react'

/**
 * VideoElement component - bridges video element to VideoContext for position/duration sync
 * This component subscribes to VideoContext position updates (causing re-renders)
 * so that VideoSphere doesn't have to
 */
export default function VideoElement({ videoElement }: { videoElement: HTMLVideoElement | null }) {
  const { seek, setDuration, setVideoReady, currentVideo, currentVideoRequestId, isPlaying } = useVideo()

  const {
    isReady,
    duration,
    play: playElement,
    pause: pauseElement,
    attachVideoElement,
    videoRef,
  } = useVideoElement(currentVideo, {
    muted: true,
    loop: false,
    reloadToken: currentVideoRequestId,
    onReady: () => setVideoReady(true),
    onTimeUpdate: (time) => seek(time), // This causes re-renders, but only in this component
    onError: () => setVideoReady(false),
  })

  const lastDurationRef = useRef(0)
  const lastPlayStateRef = useRef<{ isReady: boolean; isPlaying: boolean } | null>(null)

  // Attach the video element from VideoSphere
  useEffect(() => {
    attachVideoElement(videoElement)
  }, [videoElement, attachVideoElement])

  // Sync duration with VideoContext
  useEffect(() => {
    if (!currentVideo) {
      lastDurationRef.current = 0
      return
    }
    if (duration > 0 && duration !== lastDurationRef.current) {
      lastDurationRef.current = duration
      setDuration(duration)
    }
  }, [currentVideo, duration, setDuration])

  // Sync playback state with video element
  useEffect(() => {
    const lastState = lastPlayStateRef.current
    const stateChanged = !lastState || lastState.isReady !== isReady || lastState.isPlaying !== isPlaying

    if (!isReady) {
      // Only log once when transitioning to not ready
      if (stateChanged) {
        console.debug(['VideoElement: Skipping play/pause sync - video not ready', { isPlaying }], 'video')
      }
      lastPlayStateRef.current = { isReady, isPlaying }
      return
    }

    // Only log when state actually changes
    if (stateChanged) {
      console.debug(['VideoElement: Syncing playback state', { isPlaying, isReady }], 'video')
    }
    lastPlayStateRef.current = { isReady, isPlaying }

    if (isPlaying) {
      void playElement()
    } else {
      pauseElement()
    }
  }, [isReady, isPlaying, playElement, pauseElement])

  // Non-rendering component
  return null
}
